import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { RootState } from "../../store/rootReducer";
import {
  UpdateWorkflowPayload,
  SequencePayload,
} from "../../declarations/interface";
import Header from "./Header";
import EditWindow from "./EditWindow";
import SnapshotView from "../../components/SnapshotView";
import { actions } from "../../slices/flow";
import { getWorkflowById, updateWorkflowName } from "../../slices/workflow";
import { getRecordingById, getDomList } from "../../slices/recording";
import {
  actions as workflowActions,
  updateWorkflow,
} from "../../slices/workflow";
import AppModal from "../../components/AppModal";
import LinkContainer from "../../components/LinkContainer";
import SuccessIcon from "../../assets/images/success-tick.svg";
import { convertMsToSec, generateFlowUrl } from "../../util/UtilService";
import useSnackbar from "../../hooks/useSnackbar";

const useStyles = makeStyles((theme) => ({
  body: {
    borderTop: `0.5px solid ${theme.palette.primary40.main}`,
  },
  contentCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
}));

const FlowDetail = () => {
  const classes = useStyles();
  const { workflowId } = useParams();

  const newFlow = useSelector((state: RootState) => state.flow);
  const { workflow, status: workflowStatus } = useSelector(
    (state: RootState) => state.workflow
  );
  const { recording, dom_lists } = useSelector(
    (state: RootState) => state.recording
  );

  const [editMode, setEditMode] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const { notify, hideNotification } = useSnackbar();

  const handleModalOpen = () => {
    setModalOpen(true);
  };
  const handleModalClose = () => {
    dispatch(workflowActions.clearStatus());
    setModalOpen(false);
    setEditMode(0);
  };
  const handleSnackbarClose = () => {
    dispatch(workflowActions.clearStatus());
    hideNotification();
  };
  const handleEditFlowClick = () => {
    setEditMode(1);
  };
  const handleFlowNameUpdate = (updatedName: string) => {
    dispatch(
      updateWorkflowName({
        id: workflowId,
        name: updatedName,
      })
    );
  };
  const handleCancel = () => {
    setEditMode(0);
    dispatch(getWorkflowById(workflowId));
  };

  const fetchNextDoms = () => {
    console.log("fetching next doms");
    // if (!dom_lists || !workflow) return;
    // if (dom_lists.total > dom_lists.page * dom_lists.limit && editMode !== 2) {
    //   dispatch(
    //     getDomList({ id: workflow.recording_id, page: +dom_lists.page + 1 })
    //   );
    // }
  };

  const handleNext = () => {
    if (editMode === 1) {
      setEditMode(2);
    } else if (editMode === 2) {
      const deletedSequences: string[] = [];
      const updatedSequences: SequencePayload[] = [];
      const newSequences: SequencePayload[] = [];
      newFlow.dom_lists
        .filter((dom) => {
          if (dom.workflow_sequence_id && !dom.checked) {
            deletedSequences.push(dom.workflow_sequence_id);
          }
          return dom.checked;
        })
        .map((dom, index) => {
          const sequence = {
            dom_uuid: dom.dom_uuid || dom.snapshot_id,
            dom_position: index + 1,
            wait_type: dom.wait_type,
            delay_interval: dom.delay_interval,
            event_id: dom.event_id,
            checked: dom.checked,
            workflow_sequence_id: dom.workflow_sequence_id,
            workflow_id: dom.workflow_id,
            api_key: dom.api_key,
            download_key: dom.download_key,
            upload_enabled: dom.upload_enabled,
            dom_modifications: dom.dom_modifications,
          };
          if (sequence.workflow_sequence_id) {
            updatedSequences.push(sequence);
          } else {
            newSequences.push(sequence);
          }
        });
      const payload: UpdateWorkflowPayload = {
        workflow_id: newFlow.workflow_id,
        recording_id: newFlow.recording_id,
        project_id: newFlow.project_id,
        workflow_name: newFlow.workflow_name,
        workflow_entry_url: newFlow.workflow_entry_url,
        allow_loop: newFlow.allow_loop,
        recording_type: dom_lists?.recordingType,
        deletedSequences,
        updatedSequences,
        newSequences,
      };
      dispatch(updateWorkflow(payload));
    }
  };
  const handleBack = () => {
    setEditMode(1);
  };

  useEffect(() => {
    if (!workflowId) return;
    dispatch(getWorkflowById(workflowId));
  }, [workflowId]);

  useEffect(() => {
    if (!workflow) return;
    dispatch(getRecordingById(workflow.recording_id));
  }, [workflow]);

  // Used to create new flow
  useEffect(() => {
    if (!workflow) return;
    dispatch(getDomList({ id: workflow.recording_id, page: 1 }));
    const initialState = {
      workflow_id: workflow.workflow_id,
      recording_id: workflow.recording_id,
      project_id: workflow.project_id,
      workflow_name: workflow.workflow_name,
      workflow_entry_url: workflow.workflow_entry_url,
      allow_loop: workflow.allow_loop,
    };
    dispatch(actions.loadSnapshots(initialState));
  }, [workflow]);

  // Used to add doms to the created flow
  useEffect(() => {
    if (!dom_lists || !workflow) return;
    const transformedList = dom_lists.data.map((snapshot: any) => {
      const workflowSequence = workflow.workflow_sequences.find(
        (sequence) =>
          sequence.dom_uuid === snapshot.dom_uuid ||
          sequence.dom_uuid === snapshot.snapshot_id
      );
      if (workflowSequence) {
        let display_delay_interval = "0";
        if (workflowSequence.delay_interval !== "") {
          display_delay_interval = convertMsToSec(
            workflowSequence.delay_interval
          );
        }
        return {
          ...workflowSequence,
          ...snapshot,
          checked: true,
          delay_interval: workflowSequence.delay_interval ?? "0",
          display_delay_interval,
        };
      } else {
        return {
          ...snapshot,
          wait_type: "TIME",
          checked: false,
          delay_interval: "0",
          display_delay_interval: "0",
        };
      }
    });

    dispatch(
      actions.concatDomLists({
        append: dom_lists.page !== 1,
        list: transformedList,
      })
    );
  }, [dom_lists, workflow]);

  useEffect(() => {
    if (workflowStatus) {
      if (workflowStatus.message.includes("Workflow updated successfully")) {
        handleModalOpen();
      } else {
        notify({
          message: workflowStatus.message,
          type: workflowStatus.type,
          onClose: handleSnackbarClose,
        });
      }
    }
  }, [workflowStatus]);

  useEffect(() => {
    console.log(
      "Fetching dom lists in use effect, previous page: ",
      dom_lists?.page
    );
    if (!dom_lists || !workflow) return;
    if (dom_lists.total > dom_lists.page * dom_lists.limit) {
      dispatch(
        getDomList({ id: workflow.recording_id, page: +dom_lists.page + 1 })
      );
    }
  }, [dom_lists?.page]);

  return (
    <Box height="100%">
      {workflow && (
        <Header
          recordingName={recording?.recording_name ?? "RECORDING_NAME"}
          recordingId={recording?.recording_id}
          workflowName={workflow.workflow_name}
          editMode={editMode}
          onFlowNameUpdate={handleFlowNameUpdate}
          handleEditFlowClick={handleEditFlowClick}
        />
      )}
      {recording && (
        <Box
          className={classes.body}
          sx={{
            height: `calc(100vh - 92px)`,
          }}
        >
          {newFlow.dom_lists.length > 0 && (
            <Box height="100%">
              <SnapshotView
                createMode={editMode}
                editWindowVisible={editMode}
                recordingType={dom_lists?.recordingType ?? 0}
                viewType={editMode === 2 ? "CREATE_FLOW_VIEW" : "FLOW_VIEW"}
                fetchNext={fetchNextDoms}
                hasMore={
                  !!(
                    dom_lists &&
                    dom_lists.total > dom_lists.page * dom_lists.limit
                  ) && editMode !== 2
                }
                showAll={editMode === 1}
              />
              {editMode !== 0 && (
                <EditWindow
                  onCancel={handleCancel}
                  onNext={handleNext}
                  onBack={handleBack}
                  editMode={editMode}
                />
              )}
            </Box>
          )}
        </Box>
      )}

      {workflowStatus && workflowStatus.type === "SUCCESS" && (
        <AppModal open={modalOpen} onClose={handleModalClose}>
          <Box mb={0.5}>
            <Box textAlign="center">
              <img src={SuccessIcon} alt="success" />
            </Box>
            <Typography variant="h3" sx={{ textAlign: "center" }}>
              {workflowStatus.message}
            </Typography>
            <Box display="flex" flexDirection="column" gap="8px" mt={2.75}>
              <LinkContainer
                link={
                  workflow?.workflow_entry_url ||
                  generateFlowUrl(workflow?.workflow_id ?? "")
                }
              />
            </Box>
          </Box>
        </AppModal>
      )}
    </Box>
  );
};

export default FlowDetail;
